// Set transforms and validation for a company's customisable Opportunity Stage bands. Pure and
// React-free so the invariant logic is testable on its own.
//
// A band is stored as a label plus its END bound only. Every START is derived — the first band starts
// at 1, every other starts one point above the preceding band's end — which is why editing one end
// shifts exactly one neighbour and never cascades. The 0% (Lost) and 100% (Won) boundaries are not
// bands; the page renders them as fixed rows.

export type StageBand = {
	name: string;
	end: number;
	/**
	 * The value an opportunity is set to when it is moved into this stage — a point inside the band, not
	 * a boundary. The band's start and end go on classifying every other value.
	 */
	winProbability: number;
};

export type BandWithStart = StageBand & {start: number};

export type BandError = {
	index: number;
	field: 'name' | 'end' | 'winProbability';
	messageId: string;
};

export const FIRST_START = 1;
export const LAST_END = 99;
export const MAX_LABEL_LENGTH = 40;

const MIN_SPLITTABLE_WIDTH = 2;
// A chosen ceiling, not a geometric one: the range could hold 99 single-point bands, but a mapping that
// long stops being readable — on the preview bar most of all, where the segments share one row. Mirrored
// server-side, so the limit holds however the write arrives.
export const MAX_BANDS = 10;

const ERROR_NAME_REQUIRED = 'settings.opportunity_stages.error.name_required';
const ERROR_NAME_TOO_LONG = 'settings.opportunity_stages.error.name_too_long';
const ERROR_NAME_DUPLICATE = 'settings.opportunity_stages.error.name_duplicate';
const ERROR_RANGE_INVALID = 'settings.opportunity_stages.error.range_invalid';
const ERROR_END_REQUIRED = 'settings.opportunity_stages.error.end_required';
const ERROR_WIN_PROBABILITY_REQUIRED = 'settings.opportunity_stages.error.win_probability_required';
const ERROR_WIN_PROBABILITY_OUT_OF_BAND = 'settings.opportunity_stages.error.win_probability_out_of_band';

/** A rejection the server explained, as opposed to one that only has a correlation id. */
export type ServerRejection = {
	/** The reason, ready to show. */
	reason: string;
	/** The correlation id, for support to find the request. Absent if the message carried none. */
	correlationId?: string;
};

// The marker graphql prefixes onto a band validation reason, and the trailing "(correlationId)" that
// formatError appends to it. This literal is the other half of a contract with
// OPPORTUNITY_STAGE_BANDS_REJECTED in graphql's OpportunityStageBandErrorUtil, and the two packages
// cannot import from each other, so it is repeated rather than shared. Changing it on either side
// fails that side's tests, which is the prompt to change it here too — the cost of getting it wrong is
// that a stated reason silently reverts to an opaque modal.
const REJECTION_MARKER = 'OPPORTUNITY_STAGE_BANDS_REJECTED: ';
const TRAILING_CORRELATION_ID = /\s*\(([^()]*)\)\s*$/;

/**
 * Reads a server rejection out of a mutation error, or returns null when the error is not one of ours —
 * in which case the caller must fall back to the standard error handling rather than swallowing it.
 *
 * The arriving text is Relay's stringified error, so the reason sits somewhere inside a larger message
 * rather than at its start.
 */
export const parseServerRejection = (error: string): ServerRejection | null => {
	const at = error.indexOf(REJECTION_MARKER);
	if (at === -1) {
		return null;
	}

	// The reason runs to the end of its line; Relay appends its own context on later lines.
	const [firstLine] = error.slice(at + REJECTION_MARKER.length).split('\n');
	const withId = firstLine.match(TRAILING_CORRELATION_ID);
	if (!withId) {
		return {reason: firstLine.trim()};
	}

	return {
		reason: firstLine.slice(0, withId.index).trim(),
		correlationId: withId[1].trim() || undefined,
	};
};

/** The bands with their derived start bounds, for display and for width arithmetic. */
export const withStarts = (bands: StageBand[]): BandWithStart[] => {
	let start = FIRST_START;
	return bands.map(band => {
		const withStart = {...band, start};
		// A band being typed into has no usable end yet, and so cannot say where the next band begins.
		// It still occupies at least one point, so the next one starts one above it — the earliest it
		// could possibly begin. Deriving from the missing end instead would make every later start NaN,
		// so one half-typed value would blank a neighbour's start box and a second preview segment
		// rather than only spoiling its own row.
		start = Number.isFinite(band.end) ? band.end + 1 : start + 1;
		return withStart;
	});
};

/**
 * A band's win probability as shown on the preview bar. A part-deleted value is NaN until the user
 * finishes typing, and "NaN%" is not something to put in front of anyone.
 */
export const formatWinProbability = (winProbability: number): string =>
	Number.isFinite(winProbability) ? `${winProbability}%` : '—';

const widthOf = (band: BandWithStart): number => band.end - band.start + 1;

/**
 * A band whose win probability has been pulled back inside the given bounds. Used wherever a boundary
 * moves under a value the admin was not editing, so resizing a band never fails on account of a number
 * they did not touch. A bound that is not a number yet — a half-typed end — clamps nothing, because
 * there is no range to clamp against.
 */
const clampToBand = (band: StageBand, start: number, end: number): StageBand => {
	if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(band.winProbability)) {
		return band;
	}
	return {...band, winProbability: Math.min(Math.max(band.winProbability, start), end)};
};

/** The last band, with its derived start — the band Add always appends after. */
const lastWithStart = (bands: StageBand[]): BandWithStart | undefined => withStarts(bands)[bands.length - 1];

/** Every band's win probability pulled inside its own bounds, for use after boundaries have moved. */
const clampAllToTheirBands = (bands: StageBand[]): StageBand[] =>
	withStarts(bands).map(band =>
		clampToBand({name: band.name, end: band.end, winProbability: band.winProbability}, band.start, band.end)
	);

/**
 * Why a band cannot be appended, or null when one can.
 *
 * The reason is returned rather than a boolean so that whatever disables the control and whatever explains
 * it read from the same answer. Deriving the explanation separately is how the two drift: a reason added
 * here would silently keep disabling the control while the page went on explaining only the old ones.
 *
 * - `cap` — the mapping already holds as many bands as it may.
 * - `incomplete` — the last band has no usable bounds, because one is part-way through being typed. There
 *   is nothing to append after until it is a number again.
 */
export type AddBandBlock = 'cap' | 'incomplete' | null;

export const addBandBlock = (bands: StageBand[]): AddBandBlock => {
	if (bands.length >= MAX_BANDS) {
		return 'cap';
	}
	const last = lastWithStart(bands);
	// Room is a property of the whole set, not of the last band: every band needs a percentage point of
	// its own. A band mid-edit has no usable end to append after.
	if (!last || !Number.isFinite(last.start) || !Number.isFinite(last.end)) {
		return 'incomplete';
	}
	return null;
};

export const canAddBand = (bands: StageBand[]): boolean => addBandBlock(bands) === null;

/**
 * Appends a band after the last one. A new stage lands where the reader is looking — at the bottom of the
 * table — rather than wherever the widest band happens to be.
 *
 * Room comes from the last band while it has any to give: it is halved, the lower half taking the smaller
 * share of an odd width, and the appended band keeps the original end so the set still finishes at 99.
 * Once the last band is down to a single point there is nothing left to halve, so the boundary before it
 * is pulled down instead — and the one before that, as far back as it takes to free a point. Only the
 * minimum is taken: a band already down to one point is passed over rather than squeezed out.
 *
 * Every win probability is clamped afterwards, because appending moves boundaries the admin was not
 * editing, and a value must not be left outside the band it belongs to.
 */
export const addBand = (bands: StageBand[], newName: string): StageBand[] => {
	if (!canAddBand(bands)) {
		return bands;
	}

	const last = lastWithStart(bands) as BandWithStart;

	if (widthOf(last) >= MIN_SPLITTABLE_WIDTH) {
		const lowerEnd = last.start + Math.floor(widthOf(last) / 2) - 1;

		return clampAllToTheirBands([
			...bands.slice(0, -1),
			{name: last.name, end: lowerEnd, winProbability: last.winProbability},
			{name: newName, end: last.end, winProbability: lowerEnd + 1},
		]);
	}

	// The appended band takes the final point, and the ends before it come down by as little as possible
	// to leave every band a point of its own.
	const withAppended = [...bands, {name: newName, end: last.end, winProbability: last.end}];
	for (let index = withAppended.length - 2; index >= 0; index--) {
		if (withAppended[index].end >= withAppended[index + 1].end) {
			withAppended[index] = {...withAppended[index], end: withAppended[index + 1].end - 1};
		}
	}

	return clampAllToTheirBands(withAppended);
};

export const canDeleteBand = (bands: StageBand[]): boolean => bands.length > 1;

/**
 * Removes a band. The preceding band's end extends to absorb the vacated range; when the first band
 * goes, the following band simply becomes first and so starts at 1 by derivation.
 */
export const deleteBand = (bands: StageBand[], index: number): StageBand[] => {
	if (!canDeleteBand(bands) || index < 0 || index >= bands.length) {
		return bands;
	}

	const result = bands.filter((_unused, bandIndex) => bandIndex !== index);
	if (index > 0) {
		result[index - 1] = {...result[index - 1], end: bands[index].end};
	}
	return result;
};

/**
 * Sets one band's end. The last band's end is pinned at 99 and cannot be moved.
 *
 * Exactly two win probabilities can fall outside their band as a result: this band's, if the end moved
 * below it, and the next band's, whose derived start moved with it. Both are clamped rather than
 * reported, because the admin was editing the range, not those values — nothing further along is
 * touched, since no other start moves.
 */
export const setBandEnd = (bands: StageBand[], index: number, end: number): StageBand[] => {
	if (index < 0 || index >= bands.length || index === bands.length - 1) {
		return bands;
	}

	const result = [...bands];
	const start = withStarts(bands)[index].start;
	result[index] = clampToBand({...result[index], end}, start, end);

	const next = result[index + 1];
	result[index + 1] = clampToBand(next, end + 1, next.end);

	return result;
};

/**
 * Sets one band's win probability and nothing else — no boundary moves with it.
 *
 * Deliberately not clamped: this is the one number the admin types directly, so a value outside the
 * band is reported on commit rather than quietly rewritten into a different one.
 */
export const setBandWinProbability = (bands: StageBand[], index: number, winProbability: number): StageBand[] => {
	if (index < 0 || index >= bands.length) {
		return bands;
	}

	const result = [...bands];
	result[index] = {...result[index], winProbability};
	return result;
};

/**
 * Every invariant violation in the set, in row order. Mirrors the server's rules, which remain
 * authoritative; the page uses this to block a commit and show inline errors.
 */
export const validateBands = (bands: StageBand[]): BandError[] => {
	const errors: BandError[] = [];

	if (bands.length === 0) {
		return errors;
	}

	const seenNames = new Set<string>();
	let previousEnd = FIRST_START - 1;

	bands.forEach((band, index) => {
		const start = previousEnd + 1;
		const name = band.name.trim();
		if (name.length === 0) {
			errors.push({index, field: 'name', messageId: ERROR_NAME_REQUIRED});
		} else if (name.length > MAX_LABEL_LENGTH) {
			errors.push({index, field: 'name', messageId: ERROR_NAME_TOO_LONG});
		} else if (seenNames.has(name.toLowerCase())) {
			errors.push({index, field: 'name', messageId: ERROR_NAME_DUPLICATE});
		} else {
			seenNames.add(name.toLowerCase());
		}

		const isLast = index === bands.length - 1;
		if (!Number.isInteger(band.end)) {
			// An empty box is its own problem, reported as such. The following row is then judged against
			// the last end that was a number, so one cleared box flags one row instead of also accusing
			// its neighbour of a bad range — every comparison against NaN is false, which would otherwise
			// make the neighbour look invalid too.
			errors.push({index, field: 'end', messageId: ERROR_END_REQUIRED});
			// The win probability is not judged here. Without an end there is no band to judge it against,
			// and the row already carries an error the admin can act on — a second one about a value they
			// did not touch would blame them twice for one unfinished keystroke.
			return;
		}

		const endIsSane = band.end >= FIRST_START && band.end <= LAST_END && band.end > previousEnd;
		if (!endIsSane || (isLast && band.end !== LAST_END)) {
			// The last band's end is pinned at 99 and its field is disabled, so an error reported against
			// that row is one the user cannot act on. When what broke is its ordering against the band
			// before it — someone raised that band's end to 99 or past it — the row that can actually be
			// changed is the previous one, so the message goes there instead of to a dead end.
			const blameIsUnactionable = isLast && index > 0 && band.end <= previousEnd;
			const blameIndex = blameIsUnactionable ? index - 1 : index;
			const alreadyFlagged = errors.some(error => error.index === blameIndex && error.field === 'end');
			if (!alreadyFlagged) {
				errors.push({index: blameIndex, field: 'end', messageId: ERROR_RANGE_INVALID});
			}
			// The win probability goes unjudged for the same reason as above: a band whose own bounds do
			// not make sense cannot say whether a value sits inside it.
		} else if (!Number.isInteger(band.winProbability)) {
			errors.push({index, field: 'winProbability', messageId: ERROR_WIN_PROBABILITY_REQUIRED});
		} else if (band.winProbability < start || band.winProbability > band.end) {
			errors.push({index, field: 'winProbability', messageId: ERROR_WIN_PROBABILITY_OUT_OF_BAND});
		}
		previousEnd = band.end;
	});

	return errors;
};
